import React, { useState, useMemo } from "react";
import moment from "moment-timezone";
import { v4 as uuidV4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { type CfrSuggestion, type ListCfrTemplateItem } from "@/lib/api/cfr";
import { CommodityOptions } from "@/lib/api/commodity";
import { getUniqueStrings } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useBulkSendBatchSubmitter, type BulkSendInstancePayload } from "./useBulkSendBatchSubmitter";
import { BulkSendBatchProgressView } from "./BulkSendBatchProgressView";

interface BulkUpdatedReportSendDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    selectedSuggestions: CfrSuggestion[];
    templateData: ListCfrTemplateItem[];
}

export function BulkUpdatedReportSendDialog({
    open,
    onOpenChange,
    selectedSuggestions,
    templateData,
}: BulkUpdatedReportSendDialogProps) {
    const { toast } = useToast();
    const { phase, isSubmitting, submit, close } = useBulkSendBatchSubmitter({
        onBeforeRedirect: () => onOpenChange(false),
    });

    // Get unique crop years from templates
    const cropYearOptions = useMemo(() => {
        const uniqueYears = Array.from(new Set(templateData.map(t => t.crop_year)))
            .sort((a, b) => b - a);
        return uniqueYears.map(year => ({
            value: year.toString(),
            label: year.toString(),
        }));
    }, [templateData]);

    const currentYear = moment().year();
    const [selectedCropYear] = useState<string>(
        selectedSuggestions[0]?.crop_year?.toString() ||
        cropYearOptions.find(opt => opt.value === currentYear.toString())?.value ||
        cropYearOptions[0]?.value ||
        currentYear.toString()
    );
    const [isTestSend, setIsTestSend] = useState(false);

    // Get unique commodities from selected suggestions (read-only)
    const uniqueCommodities = useMemo(() => {
        const commodityValues = getUniqueStrings(
            selectedSuggestions
                .map(s => s.commodity)
                .filter((c): c is string => !!c)
        );
        return commodityValues.map(commodity => {
            const option = CommodityOptions.find(opt => opt.value === commodity);
            return {
                value: commodity,
                label: option?.label || commodity,
            };
        }).sort((a, b) => a.label.localeCompare(b.label));
    }, [selectedSuggestions]);

    // Template matching logic with validation
    const matchingResults = useMemo(() => {
        const selectedYear = parseInt(selectedCropYear);
        const validMatches: Array<{
            suggestion: CfrSuggestion;
            template: ListCfrTemplateItem;
        }> = [];
        const unmatchedSuggestions: CfrSuggestion[] = [];
        const ambiguousSuggestions: Array<{
            suggestion: CfrSuggestion;
            templates: ListCfrTemplateItem[];
        }> = [];

        for (const suggestion of selectedSuggestions) {
            // Find templates matching crop year, commodity, and customer_destination_id
            const matchingTemplates = templateData.filter(template =>
                template.crop_year === (suggestion.crop_year || selectedYear) &&
                template.commodity_type === suggestion.commodity &&
                template.customer_destination_id === suggestion.destination_id
            );

            if (matchingTemplates.length === 0) {
                unmatchedSuggestions.push(suggestion);
            } else if (matchingTemplates.length === 1) {
                validMatches.push({
                    suggestion,
                    template: matchingTemplates[0],
                });
            } else {
                ambiguousSuggestions.push({
                    suggestion,
                    templates: matchingTemplates,
                });
            }
        }

        return {
            validMatches,
            unmatchedSuggestions,
            ambiguousSuggestions,
        };
    }, [selectedSuggestions, templateData, selectedCropYear]);

    const handleClose = () => {
        if (isSubmitting) { return; }
        close();
        onOpenChange(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (selectedSuggestions.length === 0) {
            toast({ title: "Error", description: "No suggestions selected", variant: "destructive" });
            return;
        }
        if (matchingResults.validMatches.length === 0) {
            toast({
                title: "Error",
                description: "No valid template matches found. All suggestions are either unmatched or ambiguous.",
                variant: "destructive",
            });
            return;
        }

        // Build payloads from the validMatches list. Same shape as the
        // legacy code path; only the bulk-send transport changed.
        const payloads: BulkSendInstancePayload[] = matchingResults.validMatches.map(({ suggestion, template }) => ({
            cfr_document_id: template.id,
            group_id: uuidV4(),
            template_id: template.DocumentTemplate!.id,
            customer_id: suggestion.customer_id!,
            is_test_send: isTestSend,
            is_draft: false,
            suggestion_id: suggestion.id,
            meta_fields: {
                contract_number: suggestion.contract_number,
                contract_type: suggestion.original_meta?.contract_type,
                contract_shipping_dates: suggestion.original_meta?.shipment_range,
                contract_quantity: suggestion.contract_quantity,
            },
        }));

        await submit(payloads);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { handleClose(); } else { onOpenChange(o); } }}>
            <DialogContent className="sm:max-w-[550px]">
                <DialogHeader>
                    <DialogTitle>Bulk Send CFR Templates (Updated Report)</DialogTitle>
                </DialogHeader>

                {phase.kind !== "idle" ? (
                    <BulkSendBatchProgressView phase={phase} onClose={handleClose} />
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label>Selected Suggestions</Label>
                                <div className="text-sm text-muted-foreground">
                                    {selectedSuggestions.length} suggestion(s) selected
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label htmlFor="is_test_send">Is Test Send</Label>
                                <Checkbox
                                    id="is_test_send"
                                    checked={isTestSend}
                                    onCheckedChange={(checked) => setIsTestSend(!!checked)}
                                />
                            </div>

                            <div className="grid gap-2">
                                <Label>Commodities (from selected suggestions)</Label>
                                <div className="flex flex-wrap gap-2">
                                    {uniqueCommodities.length > 0 ? (
                                        uniqueCommodities.map((commodity) => (
                                            <Badge key={commodity.value} variant="secondary">
                                                {commodity.label}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-sm text-muted-foreground">No commodities found</span>
                                    )}
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label>Template Matching Results</Label>
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        <span className="font-medium text-green-600">
                                            {matchingResults.validMatches.length} valid match(es)
                                        </span>
                                        <span className="text-muted-foreground">- Will be queued</span>
                                    </div>
                                    {matchingResults.unmatchedSuggestions.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 text-yellow-600" />
                                            <span className="font-medium text-yellow-600">
                                                {matchingResults.unmatchedSuggestions.length} unmatched
                                            </span>
                                            <span className="text-muted-foreground">- No template found</span>
                                        </div>
                                    )}
                                    {matchingResults.ambiguousSuggestions.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="h-4 w-4 text-red-600" />
                                            <span className="font-medium text-red-600">
                                                {matchingResults.ambiguousSuggestions.length} ambiguous
                                            </span>
                                            <span className="text-muted-foreground">- Multiple templates found</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={matchingResults.validMatches.length === 0}
                            >
                                {`Send ${matchingResults.validMatches.length} Contract(s)`}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
